/**
 * Portal frontend entrypoint.
 *
 * Wires the React tree under:
 *   - `<ClerkProvider>` — auth context (RLY-013).
 *   - `<QueryClientProvider>` — server-state cache (RLY-006).
 *   - `<BrowserRouter>` — client-side routing.
 *   - `<ToastProvider>` — in-app toast surface (RLY-024).
 *
 * Standing order [04]: `VITE_CLERK_PUBLISHABLE_KEY` is loaded via
 * `loadClerkPublishableKey()` which throws in non-DEV when missing. Only
 * way to bypass auth in DEV is the explicit `VITE_ALLOW_NO_AUTH=true`
 * escape hatch — production bundles cannot reach that branch (Vite drops
 * `import.meta.env.DEV` dead code at build time).
 *
 * RLY-024 (Sprint 06): mounts the dashboard + agents + agent-detail pages
 * under `<ProtectedRoute>` + `<AppShell>`. The signed-out shell still
 * lives at `/`; signed-in users are redirected to `/dashboard` after login.
 */
import { ClerkProvider } from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";

import { App } from "./App";
import { AppShell } from "./components/app-shell";
import { ModuleGuard } from "./components/module-guard";
import { ProtectedRoute } from "./components/protected-route";
import { ToastProvider } from "./components/ui/toast";
import "./index.css";
import { loadClerkPublishableKey } from "./lib/clerk-env";
import { AgentConfigurePage } from "./routes/agent-configure";
import { AgentDetailPage } from "./routes/agent-detail";
import { AgentHistoryPage } from "./routes/agent-history";
import { AgentNewPage } from "./routes/agent-new";
import { AgentTemplatesPage } from "./routes/agent-templates";
import { AgentToolsPage } from "./routes/agent-tools";
import { AgentsListPage } from "./routes/agents-list";
import { AnalyticsFunnelPage } from "./routes/analytics-funnel";
import { AutomationsPage } from "./routes/automations";
import { ConnectionsHomePage } from "./routes/connections-home";
import { ContentAnalyticsPage } from "./routes/content-analytics";
import { ContentHomePage } from "./routes/content-home";
import { ContentPostDetailPage } from "./routes/content-post-detail";
import { ContentPostNewPage } from "./routes/content-post-new";
import { ContentSettingsPage } from "./routes/content-settings";
import { DashboardPage } from "./routes/dashboard";
import { LeadDetailPage } from "./routes/lead-detail";
import { LeadNewPage } from "./routes/lead-new";
import { LeadsHomePage } from "./routes/leads-home";
import { LeadsInboxPage } from "./routes/leads-inbox";
import { AnswerDocumentsPage } from "./routes/answer-documents";
import { AnswerHomePage } from "./routes/answer-home";
import { AnswerLibraryPage } from "./routes/answer-library";
import { AnswerQuestionnaireDetailPage } from "./routes/answer-questionnaire-detail";
import { AnswerQuestionnairesPage } from "./routes/answer-questionnaires";
import { QuoteCatalogPage } from "./routes/quote-catalog";
import { QuoteDocumentDetailPage } from "./routes/quote-document-detail";
import { QuoteDocumentsPage } from "./routes/quote-documents";
import { QuoteSequenceDetailPage } from "./routes/quote-sequence-detail";
import { QuoteSequenceNewPage } from "./routes/quote-sequence-new";
import { QuoteDraftsPage } from "./routes/quote-drafts";
import { QuoteHomePage } from "./routes/quote-home";
import { QuotePlaybooksPage } from "./routes/quote-playbooks";
import { QuoteSequencesPage } from "./routes/quote-sequences";
import { RunDetailPage } from "./routes/run-detail";
import { SettingsAuditPage } from "./routes/settings-audit";
import { SettingsBillingPage } from "./routes/settings-billing";
import { SettingsBookingPage } from "./routes/settings-booking";
import { SettingsSecretsPage } from "./routes/settings-secrets";
import { SettingsTeamPage } from "./routes/settings-team";
import { SettingsTwilioPage } from "./routes/settings-twilio";
import { SignInPage } from "./routes/sign-in";
import { SignOutPage } from "./routes/sign-out";
import { SignUpPage } from "./routes/sign-up";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element #root not found in index.html");

/**
 * Bridges Clerk's internal navigation to React Router's `useNavigate`.
 * Without routerPush/routerReplace, Clerk v5 falls back to window.location
 * even when ClerkProvider is inside BrowserRouter — React Router sees that
 * as an external navigation and remounts <SignUp>, sending a second OTP.
 */
function ClerkWithRouter({
  children,
  publishableKey,
}: {
  children: React.ReactNode;
  publishableKey: string;
}) {
  const navigate = useNavigate();
  return (
    <ClerkProvider
      publishableKey={publishableKey}
      afterSignOutUrl="/"
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
    >
      {children}
    </ClerkProvider>
  );
}

const { publishableKey } = loadClerkPublishableKey({
  VITE_CLERK_PUBLISHABLE_KEY: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
  VITE_ALLOW_NO_AUTH: import.meta.env.VITE_ALLOW_NO_AUTH,
  DEV: import.meta.env.DEV,
});

const tree = (
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        {/*
          RLY-267: opt into React Router v7 future flags so production
          ships under the same semantics validated by the test suite.
          Keep these flags in shape-parity with `ROUTER_FUTURE_FLAGS` in
          `src/routes/test-utils.tsx`.
        */}
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          {/*
            ClerkProvider is inside BrowserRouter so Clerk uses React Router's
            navigation API for multi-step flows (sign-up → verify-email).
            When it was outside, Clerk fell back to raw window.history, which
            React Router saw as an external navigation event and remounted
            SignUp, triggering a second prepareEmailAddressVerification call
            and a duplicate OTP email sent to the user.
          */}
          {publishableKey ? (
            <ClerkWithRouter publishableKey={publishableKey}>
              <Routes>
                <Route path="/" element={<App />} />
                <Route path="/sign-in/*" element={<SignInPage />} />
                <Route path="/sign-up/*" element={<SignUpPage />} />
                <Route path="/sign-out" element={<SignOutPage />} />

                {/* Authenticated portal surface (RLY-024). */}
                <Route
                  element={
                    <ProtectedRoute>
                      <AppShell />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/dashboard" element={<DashboardPage />} />

                  {/* Relay Agents module */}
                  <Route
                    path="/agents"
                    element={
                      <ModuleGuard moduleId="agents">
                        <AgentsListPage />
                      </ModuleGuard>
                    }
                  />
                  <Route
                    path="/agents/templates"
                    element={
                      <ModuleGuard moduleId="agents">
                        <AgentTemplatesPage />
                      </ModuleGuard>
                    }
                  />
                  <Route
                    path="/agents/new"
                    element={
                      <ModuleGuard moduleId="agents">
                        <AgentNewPage />
                      </ModuleGuard>
                    }
                  />
                  <Route
                    path="/agents/:id"
                    element={
                      <ModuleGuard moduleId="agents">
                        <AgentDetailPage />
                      </ModuleGuard>
                    }
                  />
                  <Route
                    path="/agents/:id/configure"
                    element={
                      <ModuleGuard moduleId="agents">
                        <AgentConfigurePage />
                      </ModuleGuard>
                    }
                  />
                  <Route
                    path="/agents/:id/history"
                    element={
                      <ModuleGuard moduleId="agents">
                        <AgentHistoryPage />
                      </ModuleGuard>
                    }
                  />
                  <Route
                    path="/agents/:id/tools"
                    element={
                      <ModuleGuard moduleId="agents">
                        <AgentToolsPage />
                      </ModuleGuard>
                    }
                  />
                  <Route path="/runs/:id" element={<RunDetailPage />} />

                  {/* Relay Leads module */}
                  <Route
                    path="/leads"
                    element={
                      <ModuleGuard moduleId="leads">
                        <LeadsHomePage />
                      </ModuleGuard>
                    }
                  />
                  <Route
                    path="/leads/inbox"
                    element={
                      <ModuleGuard moduleId="leads">
                        <LeadsInboxPage />
                      </ModuleGuard>
                    }
                  />
                  <Route
                    path="/leads/new"
                    element={
                      <ModuleGuard moduleId="leads">
                        <LeadNewPage />
                      </ModuleGuard>
                    }
                  />
                  <Route
                    path="/leads/:id"
                    element={
                      <ModuleGuard moduleId="leads">
                        <LeadDetailPage />
                      </ModuleGuard>
                    }
                  />

                  {/* Relay Content module */}
                  <Route
                    path="/content"
                    element={
                      <ModuleGuard moduleId="content">
                        <ContentHomePage />
                      </ModuleGuard>
                    }
                  />
                  <Route
                    path="/content/posts/new"
                    element={
                      <ModuleGuard moduleId="content">
                        <ContentPostNewPage />
                      </ModuleGuard>
                    }
                  />
                  <Route
                    path="/content/posts/:id"
                    element={
                      <ModuleGuard moduleId="content">
                        <ContentPostDetailPage />
                      </ModuleGuard>
                    }
                  />
                  <Route
                    path="/content/settings"
                    element={
                      <ModuleGuard moduleId="content">
                        <ContentSettingsPage />
                      </ModuleGuard>
                    }
                  />
                  <Route
                    path="/content/analytics"
                    element={
                      <ModuleGuard moduleId="content">
                        <ContentAnalyticsPage />
                      </ModuleGuard>
                    }
                  />

                  {/* Relay Quote module (M-QUOTE.3) */}
                  <Route path="/quote" element={<QuoteHomePage />} />
                  <Route path="/quote/catalog" element={<QuoteCatalogPage />} />
                  <Route path="/quote/playbooks" element={<QuotePlaybooksPage />} />
                  <Route path="/quote/documents" element={<QuoteDocumentsPage />} />
                  <Route
                    path="/quote/documents/:id"
                    element={<QuoteDocumentDetailPage />}
                  />
                  <Route path="/quote/drafts" element={<QuoteDraftsPage />} />
                  <Route path="/quote/sequences" element={<QuoteSequencesPage />} />
                  <Route path="/quote/sequences/new" element={<QuoteSequenceNewPage />} />
                  <Route
                    path="/quote/sequences/:id"
                    element={<QuoteSequenceDetailPage />}
                  />

                  {/* Relay Answer module (M-ANSWER.3) */}
                  <Route path="/answer" element={<AnswerHomePage />} />
                  <Route path="/answer/documents" element={<AnswerDocumentsPage />} />
                  <Route
                    path="/answer/questionnaires"
                    element={<AnswerQuestionnairesPage />}
                  />
                  <Route
                    path="/answer/questionnaires/:id"
                    element={<AnswerQuestionnaireDetailPage />}
                  />
                  <Route path="/answer/library" element={<AnswerLibraryPage />} />

                  <Route path="/analytics/funnel" element={<AnalyticsFunnelPage />} />
                  <Route path="/automations" element={<AutomationsPage />} />
                  <Route path="/connections" element={<ConnectionsHomePage />} />
                  <Route path="/settings/secrets" element={<SettingsSecretsPage />} />
                  <Route path="/settings/billing" element={<SettingsBillingPage />} />
                  <Route path="/settings/team" element={<SettingsTeamPage />} />
                  <Route path="/settings/audit" element={<SettingsAuditPage />} />
                  <Route path="/settings/twilio" element={<SettingsTwilioPage />} />
                  <Route path="/settings/booking" element={<SettingsBookingPage />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ClerkWithRouter>
          ) : (
            // DEV-only escape hatch (VITE_ALLOW_NO_AUTH=true). Mounts the
            // app without Clerk; signed-in surfaces won't work. The warning
            // was already emitted by loadClerkPublishableKey.
            <Routes>
              <Route path="/" element={<App />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          )}
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  </React.StrictMode>
);

ReactDOM.createRoot(rootElement).render(tree);

// `RunDetailPlaceholder` removed in RLY-025; `/runs/:id` now renders the
// real `RunDetailPage` (`./routes/run-detail.tsx`).
